import { safeFetch } from '../../../global/data/service.js'

export const BRIGHTCOVE_VIDEO_PLAYER_ID = 'k4UdWzLO6n'
export const BRIGHTCOVE_VIDEO_BLOG_PLAYER_ID = 'qAEHsSC1j9'

/**
 * Channel route config — single source of truth for all Bloomreach channel mappings.
 *
 * To add a new Bloomreach channel that uses a non-standard URL structure, add an entry here.
 * Channels that follow the standard /{channel}/* or /{channel}/preview/* convention
 * are handled automatically by the dynamic fallback in getPathAndEndpoint — no entry needed.
 *
 * Fields:
 *   aliases       - URL prefixes that belong to this channel (checked in order, first match wins)
 *   path          - SPA base path returned by getLocalPath / used for canonical URLs
 *   endpoint      - Bloomreach resourceapi channel path (may differ from path)
 *   siteName      - Bloomreach site identifier returned by getSiteName
 *   canonicalPath - Canonical URL prefix for SEO; null means use path as-is
 *   aliasBundle   - CMS resource bundle name for dynamic page aliases (null = no bundle / preview)
 *   localEndpoint - Channel endpoint when running locally under /cti-site (null = preview variants)
 */
export const CHANNEL_ROUTES = [
  {
    aliases: ['/institutional/preview'],
    path: '/institutional/preview',
    endpoint: '/institutional/preview',
    siteName: 'cti-institutional',
    canonicalPath: null,
    aliasBundle: null,
    localEndpoint: null,
  },
  {
    aliases: ['/institutional'],
    path: '/institutional',
    endpoint: '/institutional',
    siteName: 'cti-institutional',
    canonicalPath: null,
    aliasBundle: 'cti-institutional-aliases',
    localEndpoint: '/cti-site/cti-institutional',
  },
  {
    aliases: ['/insights/preview'],
    path: '/insights/preview',
    endpoint: '/insights/preview',
    siteName: 'cti-advisor-spa',
    canonicalPath: null,
    aliasBundle: null,
    localEndpoint: null,
  },
  {
    // Blog content at /insights/latest-insights and /insights/market-perspectives
    // is served by the blogspa channel — path and endpoint intentionally differ.
    aliases: ['/insights/latest-insights', '/insights/market-perspectives'],
    path: '/insights',
    endpoint: '/blogspa',
    siteName: 'cti-blogspa',
    canonicalPath: null,
    aliasBundle: null,
    localEndpoint: '/cti-site/blogspa',
  },
  {
    aliases: ['/blogspa/preview'],
    path: '/blogspa/preview',
    endpoint: '/blogspa/preview',
    siteName: 'cti-blogspa',
    canonicalPath: null,
    aliasBundle: null,
    localEndpoint: null,
  },
  {
    aliases: ['/blogspa'],
    path: '/blogspa',
    endpoint: '/blogspa',
    siteName: 'cti-blogspa',
    canonicalPath: '/insights',
    aliasBundle: null,
    localEndpoint: '/cti-site/blogspa',
  },
  {
    aliases: ['/insights'],
    path: '/insights',
    endpoint: '/insights',
    siteName: 'cti-advisor-spa',
    canonicalPath: null,
    aliasBundle: 'cti-insights-aliases',
    localEndpoint: '/cti-site/cti-advisor-spa',
  },
  {
    aliases: ['/advisor-insights/preview'],
    path: '/advisor-insights/preview',
    endpoint: '/advisor-insights/preview',
    siteName: 'cti-advisor-spa',
    canonicalPath: null,
    aliasBundle: null,
    localEndpoint: null,
  },
  {
    aliases: ['/advisor-insights'],
    path: '/advisor-insights',
    endpoint: '/advisor-insights',
    siteName: 'cti-advisor-spa',
    canonicalPath: null,
    aliasBundle: 'cti-advisor-insights-aliases',
    localEndpoint: '/cti-site/cti-advisor-spa',
  },
  {
    aliases: ['/advisor/preview'],
    path: '/advisor/preview',
    endpoint: '/advisor/preview',
    siteName: 'cti-advisorspa',
    canonicalPath: null,
    aliasBundle: null,
    localEndpoint: null,
  },
  {
    // Section roots and legal/auth paths are kept static so they resolve correctly
    // even if the CMS alias bundle is unreachable. One-off vanity/campaign URLs
    // (e.g. /dare2tri, /game-changer) are managed via the cti-advisor-aliases CMS
    // resource bundle and require no frontend release to add.
    //
    // Note: /feature/etf-advantage and /feature/fixed-income-opportunity must stay
    // here because the /feature static entry (featurepages channel) would otherwise
    // match them before the CMS lookup runs.
    aliases: [
      '/advisor',
      '/about',
      '/resources',
      '/terms-of-use',
      '/privacy-security',
      '/corporate-entities',
      '/disclosures',
      '/privacy-statement',
      '/user-agreement',
      '/search',
      '/login',
      '/register',
      '/my-profile',
      '/investment-products',
      '/investment-capabilities',
      '/subscribe',
      '/feature/etf-advantage',
      '/feature/fixed-income-opportunity',
    ],
    path: '/advisor',
    endpoint: '/advisor',
    siteName: 'cti-advisorspa',
    canonicalPath: '',
    aliasBundle: 'cti-advisor-aliases',
    localEndpoint: '/cti-site/cti-advisor',
  },
  {
    aliases: ['/investorspa/preview'],
    path: '/investorspa/preview',
    endpoint: '/investorspa/preview',
    siteName: 'cti-investorspa',
    canonicalPath: null,
    aliasBundle: null,
    localEndpoint: null,
  },
  {
    aliases: ['/investorspa', '/investor'],
    path: '/investorspa',
    endpoint: '/investorspa',
    siteName: 'cti-investorspa',
    canonicalPath: '/investor',
    aliasBundle: null,
    localEndpoint: '/cti-site/cti-investorspa',
  },
  {
    aliases: ['/feature/preview'],
    path: '/feature/preview',
    endpoint: '/feature/preview',
    siteName: 'cti-featurepages',
    canonicalPath: null,
    aliasBundle: null,
    localEndpoint: null,
  },
  {
    aliases: ['/feature'],
    path: '/feature',
    endpoint: '/feature',
    siteName: 'cti-featurepages',
    canonicalPath: null,
    aliasBundle: 'cti-feature-aliases',
    localEndpoint: '/cti-site/cti-featurepages',
  },
]

// ---------------------------------------------------------------------------
// Dynamic alias loading from CMS
// ---------------------------------------------------------------------------

/**
 * Builds the halapi URL for a channel's alias resource bundle.
 * Uses the channel's own endpoint so each fetch is scoped to its own Bloomreach site.
 * Falls back to localEndpoint when running under /cti-site in local development.
 */
const _getChannelAliasURL = (channel) => {
  const isLocalDev = window.location.pathname.startsWith('/cti-site')
  const base = isLocalDev ? channel.localEndpoint : channel.endpoint
  return `${base}/halapi/documents?_scope=/content/documents/administration/cti-common/${channel.aliasBundle}`
}

/**
 * Dynamic aliases fetched from per-channel CMS resource bundles.
 * Stored as a pre-sorted array of [urlPrefix, channelRoute] pairs, longest prefix
 * first, so more-specific paths (e.g. /investor/new-page) always win over shorter
 * ones (e.g. /new-page) even across different channel bundles.
 */
let _dynamicAliases = []
let _aliasesLoaded = false
let _aliasesPromise = null

/**
 * Fetches each channel's alias resource bundle from Bloomreach CMS in parallel.
 * Idempotent — returns the same promise on subsequent calls.
 * Channels whose bundle is missing or unreachable are skipped silently.
 *
 * Bloomreach setup — one resource bundle per channel
 * (name matches the aliasBundle field on each CHANNEL_ROUTES entry):
 *
 *   /content/documents/administration/cti-common/cti-advisor-aliases
 *   /content/documents/administration/cti-common/cti-investor-aliases
 *   /content/documents/administration/cti-common/cti-institutional-aliases
 *   ... etc.
 *
 * Within each bundle, add one entry per new page alias:
 *   key   = URL prefix  (e.g. /new-campaign)
 *   value = (unused — the channel is implied by which bundle the entry lives in)
 *
 * CMS users only ever touch their own channel's bundle. The same URL prefix can
 * safely appear in both the advisor and investor bundles — each independently
 * routes that path to its own channel. No cross-channel coordination required.
 *
 * New entries take effect on next page load — no frontend release needed.
 */
export const loadChannelAliases = () => {
  if (_aliasesPromise) return _aliasesPromise

  // One fetch per unique aliasBundle — deduplicates entries that share a bundle
  // (e.g. blogspa entries) and skips preview variants where aliasBundle is null.
  const channels = CHANNEL_ROUTES.filter(
    (c, i, arr) =>
      c.aliasBundle &&
      arr.findIndex((x) => x.aliasBundle === c.aliasBundle) === i
  )

  _aliasesPromise = Promise.all(
    channels.map(
      (channel) =>
        safeFetch(_getChannelAliasURL(channel))
          .then((r) => r.json())
          .then((data) => {
            const raw = data?._embedded?.documents?.[0]?.bundles?.default ?? {}
            // Each key in the bundle is a URL prefix owned by this channel.
            // Store the channel object directly — no secondary lookup needed.
            return Object.keys(raw).map((alias) => [alias, channel])
          })
          .catch(() => []) // Bundle missing or CMS unreachable — skip this channel.
    )
  )
    .then((results) => {
      // Merge all channel alias lists, sort longest prefix first so more-specific
      // paths (e.g. /investor/new-page) always win over shorter ones (/new-page).
      _dynamicAliases = results.flat().sort(([a], [b]) => b.length - a.length)
    })
    .finally(() => {
      _aliasesLoaded = true
    })

  return _aliasesPromise
}

/** Returns true once loadChannelAliases() has resolved (or failed). */
export const areAliasesLoaded = () => _aliasesLoaded

/**
 * Finds the matching channel config for a given pathname.
 * Checks static CHANNEL_ROUTES first, then CMS dynamic aliases.
 * Returns null if no explicit config matches (use dynamic fallback instead).
 */
const findChannelRoute = (pathname) => {
  // Static config takes precedence (ordered for specificity).
  for (const channel of CHANNEL_ROUTES) {
    if (channel.aliases.some((alias) => pathname.startsWith(alias))) {
      return channel
    }
  }
  // Dynamic aliases from CMS — pre-sorted longest-first at load time.
  // Each entry is [urlPrefix, channelRoute] — the channel object is stored directly.
  for (const [alias, channel] of _dynamicAliases) {
    if (pathname.startsWith(alias)) {
      return channel
    }
  }
  return null
}

/**
 * Known Bloomreach channel prefixes that follow the standard /{channel}/* convention
 * and are safe to derive path/endpoint from automatically.
 * Any path whose first segment is NOT in this set is treated as a vanity/campaign URL
 * and falls back to the default channel (cti-advisorspa → /advisor) instead of
 * incorrectly using the vanity slug as a channel name.
 */
const KNOWN_CHANNEL_PREFIXES = new Set(
  CHANNEL_ROUTES.map((c) => c.path.split('/').filter(Boolean)[0])
)

/**
 * Dynamic fallback: derives { path, endpoint } from URL segments for channels
 * that follow the standard /{channel}/* or /{channel}/preview/* convention.
 * New Bloomreach channels added with a clean URL structure are recognized automatically.
 * Falls back to the advisor channel for unrecognised root-level paths (vanity/campaign URLs
 * not yet present in static aliases or CMS bundle).
 */
const derivePathAndEndpoint = (pathname) => {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return { path: '/advisor', endpoint: '/advisor' }

  const channelPath = `/${segments[0]}`

  // If the first segment is not a known channel prefix, this is likely a vanity/campaign
  // URL whose CMS alias entry is missing. Fall back to the advisor channel so halapi
  // calls use /advisor/halapi/... rather than the vanity slug.
  if (!KNOWN_CHANNEL_PREFIXES.has(segments[0])) {
    return { path: '/advisor', endpoint: '/advisor' }
  }

  if (segments[1] === 'preview') {
    return {
      path: `${channelPath}/preview`,
      endpoint: `${channelPath}/preview`,
    }
  }

  return { path: channelPath, endpoint: channelPath }
}

export const getLocalPath = (pathname) => {
  return getPathAndEndpoint(pathname).path
}

export const getPathAndEndpoint = (pathname = globalThis.location.pathname) => {
  if (pathname.startsWith('/cti-site')) {
    const pathSplit = pathname.split('/')
    if (pathSplit[2] === '_cmsinternal') {
      return {
        path: `/${pathSplit[1]}/${pathSplit[2]}/${pathSplit[3]}`,
        endpoint: `/${pathSplit[1]}/${pathSplit[3]}`,
      }
    } else if (pathSplit[3] === 'preview') {
      return {
        path: `/${pathSplit[1]}/${pathSplit[2]}/${pathSplit[3]}`,
        endpoint: `/${pathSplit[1]}/${pathSplit[2]}/${pathSplit[3]}`,
      }
    } else {
      return {
        path: `/${pathSplit[1]}/${pathSplit[2]}`,
        endpoint: `/${pathSplit[1]}/${pathSplit[2]}`,
      }
    }
  }

  // Check explicit channel config (handles aliases and path/endpoint overrides).
  const channel = findChannelRoute(pathname)
  if (channel) {
    return { path: channel.path, endpoint: channel.endpoint }
  }

  // Dynamic fallback: new channels following /{channel}/* convention work automatically.
  return derivePathAndEndpoint(pathname)
}

/** Maps special site names to their shared/legacy names. */
const SHARED_SITE_NAME_MAP = {
  'cti-advisorspa': 'cti-advisor',
  'cti-investorspa': 'cti-investor',
  'cti-blogspa': 'blogspa',
}

export const getSharedSiteName = (pathname = globalThis.location.pathname) => {
  const siteName = getSiteName(pathname)
  return SHARED_SITE_NAME_MAP[siteName] ?? siteName
}

export const getSiteName = (pathname = globalThis.location.pathname) => {
  if (pathname.startsWith('/cti-site')) {
    return getLocalPath(pathname)
      .replace('/cti-site/', '')
      .replace('/preview', '')
  }

  // /preview/{channel}/... — strip leading /preview and look up the channel config.
  if (pathname.startsWith('/preview/')) {
    const withoutPreview = pathname.replace(/^\/preview/, '')
    const channel = findChannelRoute(withoutPreview)
    if (channel) return channel.siteName
  }

  // Explicit channel config lookup.
  const channel = findChannelRoute(pathname)
  if (channel) return channel.siteName

  // Dynamic fallback: derive a site name from the first path segment.
  // Unrecognised root-level paths (vanity/campaign URLs) fall back to the advisor
  // channel site name, consistent with the fallback in derivePathAndEndpoint.
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return 'cti-advisorspa'
  return KNOWN_CHANNEL_PREFIXES.has(segments[0])
    ? `cti-${segments[0]}`
    : 'cti-advisorspa'
}

export const isLocal = () => {
  return globalThis.location.port
}

export const isDev = () => {
  return globalThis.location.origin.indexOf('www.dev') > -1
}

export const isQa = () => {
  return globalThis.location.origin.indexOf('www.qa') > -1
}

export const isQaWs3 = () => {
  return globalThis.location.origin.indexOf('www-ws3.qa') > -1
}

export const isQaWs4 = () => {
  return globalThis.location.origin.indexOf('www-ws4.qa') > -1
}

export const isQaWs5 = () => {
  return globalThis.location.origin.indexOf('www-ws5.qa') > -1
}

export const isPreview = () => {
  return globalThis.location.origin.indexOf('www-preview.') > -1
}

export const isPreprod = () => {
  return globalThis.location.origin.indexOf('www-preprod.') > -1
}

export const isProduction = () => {
  return (
    globalThis.location.origin.indexOf('www.columbia') > -1 ||
    globalThis.location.origin.indexOf('www-preview.') > -1 ||
    globalThis.location.origin.indexOf('www-preprod.') > -1
  )
}

export const isProduct = () => {
  return globalThis.location.pathname.indexOf('/strategies-solutions') > -1
}

export const isIntegratedProduct = () => {
  return globalThis.location.pathname.indexOf('/integrated-equity') > -1
}

const getInvestorPathname = (pathname) => {
  return pathname
    .split('/')
    .filter((item) => item !== 'investor')
    .join('/')
}

export const getDebugConfig = () => {
  let endpoint

  if (isLocal()) {
    endpoint =
      'http://localhost:8081' + getPathAndEndpoint().endpoint + '/resourceapi'
  } else if (isDev()) {
    endpoint =
      'https://www.dev.columbiathreadneedleus.com' +
      getPathAndEndpoint().endpoint +
      '/resourceapi'
  } else if (isQaWs3()) {
    endpoint =
      'https://www-ws3.qa.columbiathreadneedleus.com' +
      getPathAndEndpoint().endpoint +
      '/resourceapi'
  } else if (isQaWs4()) {
    endpoint =
      'https://www-ws4.qa.columbiathreadneedleus.com' +
      getPathAndEndpoint().endpoint +
      '/resourceapi'
  } else if (isQaWs5()) {
    endpoint =
      'https://www-ws5.qa.columbiathreadneedleus.com' +
      getPathAndEndpoint().endpoint +
      '/resourceapi'
  } else if (isQa()) {
    endpoint =
      'https://www.qa.columbiathreadneedleus.com' +
      getPathAndEndpoint().endpoint +
      '/resourceapi'
  } else if (isPreview()) {
    endpoint =
      'https://www-preview.columbiathreadneedleus.com' +
      getPathAndEndpoint().endpoint +
      '/resourceapi'
  } else if (isPreprod()) {
    endpoint =
      'https://www-preprod.columbiathreadneedleus.com' +
      getPathAndEndpoint().endpoint +
      '/resourceapi'
  } else if (isProduction()) {
    endpoint =
      'https://www.columbiathreadneedleus.com' +
      getPathAndEndpoint().endpoint +
      '/resourceapi'
  }

  let origin

  if (isLocal()) {
    origin = undefined
  } else if (isDev()) {
    origin = 'https://cms.dev.ameriprise.com'
  } else if (isQaWs3()) {
    origin = 'https://cms-ws3.qa.ameriprise.com'
  } else if (isQaWs4()) {
    origin = 'https://cms-ws4.qa.ameriprise.com'
  } else if (isQaWs5()) {
    origin = 'https://cms-ws5.qa.ameriprise.com'
  } else if (isQa()) {
    origin = 'https://cms.qa.ameriprise.com'
  } else if (isPreview() || isPreprod() || isProduction()) {
    origin = 'https://cms.ameriprise.com'
  }

  const localPath = getLocalPath()
  const pathname = globalThis.location.pathname
  const strippedPathname =
    pathname.startsWith(localPath + '/') || pathname === localPath
      ? pathname.slice(localPath.length)
      : pathname

  const path = `${
    pathname.startsWith('/investor/')
      ? getInvestorPathname(pathname)
      : strippedPathname
  }${globalThis.location.search}`

  return {
    PATH: path,
    ENDPOINT: endpoint,
    DEBUG: false,
    ORIGIN: origin,
  }
}
